import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthService } from '../auth-service';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import {
  AttributeKeyDto,
  AttributeValueDto,
  CreateValuesRequest,
  ReorderAttributesRequest,
  ProductAttributesResponse,
  ReorderItem,
  GlobalAttributesResponse,
  SaveProductAttributesRequest,
  ExtendedAttribute
} from './productAttributes.model';

@Injectable({ providedIn: 'root' })
export class AttributesService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private API = environment.productsApiUrl;

  private getHeaders(): HttpHeaders {
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders().set('Accept', 'application/json');
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  getGlobalAttributes(): Observable<GlobalAttributesResponse> {
    return this.http.get<GlobalAttributesResponse>(`${this.API}/global/product/attributes`, {
      headers: this.getHeaders(),
    });
  }

  saveProductAttributes(productId: number, list: { attr: ExtendedAttribute, index: number }[]): Observable<void> {
    const body: SaveProductAttributesRequest = {
      attributes: list.map((item) => this.mapToDto(item.attr, item.index))
    };
    return this.http.post<void>(`${this.API}/products/${productId}/attributes`, body, {
      headers: this.getHeaders(),
    });
  }

  private mapToDto(attr: ExtendedAttribute, index: number) {
    const correctedOrder = index + 1;
    if (attr.isGlobal) {
      return {
        type: 'global',
        order: correctedOrder,
        globalAttributeKeyId: attr.attributeKey.id,
      };
    }
    return {
      type: 'local',
      order: correctedOrder,
      attributeKey: {
        titleEn: attr.attributeKey.titleEn || undefined,
        titleAr: attr.attributeKey.titleAr || null,
      },
      attributeValues: attr.attributeValues.map((v) => ({
        titleEn: v.titleEn || undefined,
        titleAr: v.titleAr || null,
      })),
    };
  }

  getProductAttributes(productId: number): Observable<ProductAttributesResponse> {
    return this.http.get<ProductAttributesResponse>(`${this.API}/products/${productId}/attributes`, {
      headers: this.getHeaders(),
    });
  }

  updateAttributeKey(productId: number, keyId: number, body: AttributeKeyDto): Observable<void> {
    return this.http.put<void>(`${this.API}/products/${productId}/attributes/${keyId}`, { ...body, titleEn: body.titleEn || undefined }, {
      headers: this.getHeaders(),
    });
  }

  updateAttributeValue(productId: number, keyId: number, valueId: number, body: AttributeValueDto): Observable<void> {
    return this.http.put<void>(`${this.API}/products/${productId}/attributes/${keyId}/values/${valueId}`, { ...body, titleEn: body.titleEn || undefined }, {
      headers: this.getHeaders(),
    });
  }

  deleteAttributeKey(productId: number, keyId: number): Observable<void> {
    return this.http.delete<void>(`${this.API}/products/${productId}/attributes/${keyId}`, {
      headers: this.getHeaders(),
    });
  }

  deleteAttributeValue(productId: number, keyId: number, valueId: number): Observable<void> {
    return this.http.delete<void>(`${this.API}/products/${productId}/attributes/${keyId}/values/${valueId}`, {
      headers: this.getHeaders(),
    });
  }

  createAttributeValues(productId: number, keyId: number, values: AttributeValueDto[]): Observable<void> {
    const body: CreateValuesRequest = { attributes: values.map((v) => ({ ...v, titleEn: v.titleEn || undefined })) };
    return this.http.post<void>(`${this.API}/products/${productId}/attributes/${keyId}/values`, body, {
      headers: this.getHeaders(),
    });
  }

  reorderAttributes(productId: number, items: ReorderItem[]): Observable<void> {
    const body: ReorderAttributesRequest = { attributes: items };
    return this.http.put<void>(`${this.API}/products/${productId}/attributes/reorder`, body, {
      headers: this.getHeaders(),
    });
  }
}